import {
  Flex,
  IconButton,
  type LayoutProps,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from "@chakra-ui/react"
import { useIntl } from "react-intl"

import { MultiplyIcon } from "../Icons"
import { H4 } from "../Typographies"

type Props = React.PropsWithChildren<{
  isOpen: boolean
  onClose: () => void
  title: React.ReactNode
  footer?: React.ReactNode
  maxWidth?: LayoutProps["maxWidth"]
}>

// Full-screen variant of the shared modal (ADR 0001, lot 4): white page, header closed by the
// red line under the title, as wide as the content, and the cross alone to close it.
export const FullScreenModal: React.FC<Props> = ({
  isOpen,
  onClose,
  children,
  title,
  footer,
  maxWidth,
}) => {
  const intl = useIntl()

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="full" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent
        borderRadius={0}
        border="none"
        display="flex"
        flexDir="column"
        alignItems="center"
        bg="surface"
        w="100%"
      >
        {/* Same width and side margins as the content: the red line stops with the form. */}
        <ModalHeader maxW={maxWidth} w="100%" px="6" py="0">
          <Flex
            justifyContent="space-between"
            alignItems="center"
            gap="6"
            py="5"
            borderBottom="2px solid"
            borderColor="accent"
          >
            <H4 fontSize="xl">{title}</H4>
            <IconButton
              variant="transparent"
              aria-label={intl.formatMessage({ id: "close" })}
              icon={<MultiplyIcon boxSize={5} />}
              onClick={onClose}
              flexShrink={0}
              // No button minimum height (48px in the theme): it would stretch the header.
              minW="auto"
              minH="auto"
              h="auto"
              p="0"
              color="text"
              _hover={{ color: "accent" }}
            />
          </Flex>
        </ModalHeader>
        <ModalBody maxW={maxWidth} w="100%">
          {children}
        </ModalBody>
        {footer && (
          <ModalFooter w="100%" bg="surface" padding={0}>
            {footer}
          </ModalFooter>
        )}
      </ModalContent>
    </Modal>
  )
}
